import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { ProjectConnectionGroupCreateRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createGroupFormPermissions, toGroupPermissionsRequest } from "@/helpers/system-groups/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { projectConnectionGroupMutations } from "@/queries/project-connection-groups/mutations";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import {
  type CreateOrEditProjectConnectionGroupFormValues,
  ProjectConnectionGroupForm,
} from "./ProjectConnectionGroupForm";

export function CreateProjectConnectionGroupPage(): React.ReactNode {
  const { id: connectionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const connectionQueries = useProjectConnectionQueries();
  const permissionQueries = usePermissionQueries();
  const connectionQuery = useQuery(connectionQueries.details(connectionId!));
  const defaultPermissionsQuery = useQuery(permissionQueries.listDefaultTypes());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const createGroup = projectConnectionGroupMutations.useAddProjectConnectionGroup(connectionId!);

  const error = !connectionId || connectionQuery.error || defaultPermissionsQuery.error || rolesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (connectionQuery.isPending || defaultPermissionsQuery.isFetching || rolesQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const handleSubmit = async (values: CreateOrEditProjectConnectionGroupFormValues) => {
    if (!values.image?.length) {
      showFlashToast({ variant: "alert", message: t("page.project-connection-groups.create.error") });

      return;
    }
    const image = await uploadFormImage(values.image[0]);
    if (!image) {
      return;
    }
    const payload: ProjectConnectionGroupCreateRequest = {
      type: values.type,
      name: values.name,
      description: values.description,
      imageId: image.id,
      defaultPostingAudience: values.audience,
      permissions: toGroupPermissionsRequest(values.permissions),
      icon: values.icon!,
      postingLevel: values.postingLevel,
    };
    await createGroup.mutateAsync(payload);
    void navigate(routes.projectConnections.list());
  };

  return (
    <ProjectConnectionGroupForm
      connectionId={connectionId}
      isEditMode={false}
      projectConnection={connectionQuery.data}
      languageId={sessionUser.uiLanguage.id}
      defaultValues={{
        type: "interest",
        name: "",
        description: "",
        icon: undefined,
        image: [],
        postingLevel: "projectAndProjectConnection",
        audience: "projectConnection",
        permissions: createGroupFormPermissions(rolesQuery.data ?? [], defaultPermissionsQuery.data ?? [], undefined),
      }}
      onSubmit={handleSubmit}
      onDelete={() => Promise.resolve()}
      isSubmitting={isUploadingImage || createGroup.isPending}
    />
  );
}
